import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, ShieldCheck, Truck, Award } from "lucide-react";
import api from "../lib/api";
import { SareeCard } from "../components/SareeCard";
import { DiwaliBanner } from "../components/DiwaliBanner";
import { LookOfTheWeek } from "../components/LookOfTheWeek";

const HERO = "https://images.unsplash.com/photo-1727430228383-aa1fb59db8bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const CATEGORY_IMAGES = {
  Banarasi: "https://images.unsplash.com/photo-1651132164857-b61aa4cf7472?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  Kanjeevaram: "https://images.unsplash.com/photo-1732381917604-bc8f046965ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "Pure Silk": "https://images.unsplash.com/photo-1610189012906-4c0aa9b9781e?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "Chanderi Cotton": "https://images.unsplash.com/photo-1610030468706-9a6dbad49b0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  Georgette: "https://images.unsplash.com/photo-1614940685083-c5409b57da6e?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "Tashore Silk": "https://static.prod-images.emergentagent.com/jobs/dad8f5be-eba7-472a-8108-2cc5a1a5019c/images/49966cd7d00588172847eb85e1756ab87235be442c30fafc749252f8ab6ef1d1.jpeg",
  "Bonolata Silk": "https://static.prod-images.emergentagent.com/jobs/dad8f5be-eba7-472a-8108-2cc5a1a5019c/images/34d27c9dbc849494187ce820b36a2652e042d988cb2cce139d0ae7efb0d4c83f.jpeg",
  Begumpuri: "https://static.prod-images.emergentagent.com/jobs/dad8f5be-eba7-472a-8108-2cc5a1a5019c/images/cb39fac62ce468fe4c3dcf23019296402df9020a4921df3593e926a842a0cb06.jpeg",
  "Mall Cotton": "https://static.prod-images.emergentagent.com/jobs/dad8f5be-eba7-472a-8108-2cc5a1a5019c/images/a670a1ac5b9ca0ec1a50f744071abf486165c52cd4c73e31aa4aaf2f612509c8.jpeg",
  Chikankari: "https://static.prod-images.emergentagent.com/jobs/dad8f5be-eba7-472a-8108-2cc5a1a5019c/images/3c9a3631b2679096daa98dafedf69ffaaab8f0a98eb8a5d901b8b930b1783f6d.jpeg",
};

const FESTIVE_EDITS = [
  { name: "Bridal", tagline: "For your once-in-a-lifetime day", image: "https://images.unsplash.com/photo-1651132164857-b61aa4cf7472?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { name: "Diwali", tagline: "Festival of lights, festival of gold", image: "https://images.unsplash.com/photo-1727430228383-aa1fb59db8bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { name: "Wedding", tagline: "Guest-of-honour grandeur", image: "https://images.unsplash.com/photo-1732381917604-bc8f046965ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

export default function Landing() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products").then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      <DiwaliBanner />
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative z-10"
          >
            <span className="caption">Heritage weaves · AI draping studio</span>
            <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 text-[#FAF7F0]">
              Drape the <span className="gold-text italic">timeless.</span><br />
              See it on <span className="gold-text italic">you</span> first.
            </h1>
            <p className="text-base text-body mt-6 max-w-lg leading-relaxed text-muted-foreground">
              Tashora brings you handwoven Banarasi, Kanjeevaram, Tashor, Chikkan Kari, Bonolata Silk,
              Mall Cotton, Begumpuri and pure-silk sarees — and lets you upload a photo to preview any
              drape on yourself with our AI Virtual Draping Studio.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link
                to="/collection"
                data-testid="hero-explore-button"
                className="inline-flex items-center gap-2 bg-gold text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors"
              >
                Explore Collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/try-on"
                data-testid="hero-tryon-button"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold-muted px-6 py-3 rounded-full hover:bg-gold/10 transition-colors"
              >
                <Sparkles className="w-4 h-4 text-gold" /> AI Virtual Try-On
              </Link>
            </div>
            <div className="flex gap-8 mt-12">
              {[["10+", "Handloom weaves"], ["100%", "Pure silk & zari"], ["AI", "Try before you buy"]].map(([a, b]) => (
                <div key={b}>
                  <p className="font-serif text-3xl gold-text">{a}</p>
                  <p className="text-xs text-muted-foreground mt-1">{b}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative rounded-xl overflow-hidden gold-glow aspect-[4/5] max-w-md ml-auto">
              <img src={HERO} alt="Featured saree" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass rounded-lg p-4 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-gold shrink-0" />
                <p className="text-xs text-gold-muted">Loved this drape? Upload your photo and see it worn by you in seconds.</p>
              </div>
            </div>
            <div className="hidden lg:block absolute -top-6 -left-6 w-24 h-24 rounded-full bg-gold/10 blur-2xl animate-float" />
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-gold/12 bg-obsidian-2">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[[Award, "Certified authenticity", "Silk mark & zari purity"], [Truck, "Insured shipping", "Worldwide, fully tracked"], [ShieldCheck, "Open-package policy", "Check before you accept"]].map(([Icon, t, s]) => (
            <div key={t} className="flex items-center gap-3">
              <Icon className="w-6 h-6 text-gold shrink-0" />
              <div>
                <p className="text-sm text-gold-muted">{t}</p>
                <p className="text-xs text-muted-foreground">{s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="caption">The weaves</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-pearl mt-2 text-gold-light">Shop by tradition</h2>
          </div>
          <Link to="/collection" className="hidden sm:inline-flex items-center gap-1 text-sm text-gold-muted hover:text-gold">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {categories.map((cat, i) => (
            <Link
              key={cat}
              to={`/collection?category=${encodeURIComponent(cat)}`}
              data-testid={`category-card-${cat.toLowerCase().replace(/\s/g, "-")}`}
              className="group relative rounded-lg overflow-hidden aspect-[3/4] gold-border"
            >
              <img src={CATEGORY_IMAGES[cat]} alt={cat} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="font-serif text-lg text-gold-light">{cat}</p>
                <p className="text-[11px] text-gold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Discover <ArrowRight className="w-3 h-3" /></p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FESTIVE EDITS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-4">
        <div className="mb-10">
          <span className="caption">Curated edits</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-gold-light mt-2">Shop the occasion</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {FESTIVE_EDITS.map((edit, i) => (
            <motion.div
              key={edit.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <Link
                to={`/collection?collection=${edit.name}`}
                data-testid={`festive-edit-${edit.name.toLowerCase()}`}
                className="group relative block rounded-xl overflow-hidden aspect-[4/3] gold-border"
              >
                <img src={edit.image} alt={edit.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="caption">The {edit.name} Edit</p>
                  <h3 className="font-serif text-2xl text-gold-light mt-1">{edit.tagline}</h3>
                  <span className="inline-flex items-center gap-1 text-sm text-gold mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AI TRY-ON BAND */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="relative rounded-2xl overflow-hidden bg-obsidian-2 gold-border grain">
          <div className="grid lg:grid-cols-2 gap-0 items-center">
            <div className="p-8 sm:p-12 lg:p-16">
              <span className="caption">The Tashora difference</span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 text-gold-light leading-tight">
                Wear it before you <span className="gold-text italic">own it.</span>
              </h2>
              <p className="text-muted-foreground mt-5 max-w-md leading-relaxed">
                Upload a single portrait and our AI drapes any saree from our atelier onto you —
                realistically, in seconds. No fitting room needed.
              </p>
              <Link
                to="/try-on"
                data-testid="band-tryon-button"
                className="inline-flex items-center gap-2 mt-8 bg-gold text-obsidian font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors"
              >
                <Sparkles className="w-4 h-4" /> Open the Draping Studio
              </Link>
            </div>
            <div className="relative h-full min-h-[320px]">
              <img src="https://images.unsplash.com/photo-1619516388835-2b60acc4049e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Try on" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-obsidian-2 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-20">
        <div className="mb-10">
          <span className="caption">Curated for you</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-gold-light mt-2">Signature drapes</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p, i) => <SareeCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      <LookOfTheWeek />
    </div>
  );
}
